import React, { useState } from 'react';
import { 
  MapPin, 
  TrendingUp, 
  Shield, 
  Calendar,
  CheckCircle,
  ArrowRight,
  Phone,
  Mail,
  Bed,
  Bath,
  Maximize,
  Waves,
  Star,
  ChevronDown
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LandingImmo = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    budget: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const [activeUnit, setActiveUnit] = useState(0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(false);

    try {
      const response = await fetch(`${BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          service: 'Investissement Immobilier - Landing Instagram',
          source: 'Instagram Ad'
        })
      });

      if (response.ok) {
        setIsSubmitted(true);
        setFormData({ name: '', email: '', phone: '', budget: '', message: '' });
      } else {
        // Server error - show error message but still thank the user
        console.error('Server error:', response.status);
        setSubmitError(true);
      }
    } catch (error) {
      console.error('Error:', error);
      setSubmitError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const units = [
    { name: 'Loft 1 Chambre', price: '116 900 €', beds: 1, baths: 1, size: 60, pool: '2×3m', roi: '12-14%' },
    { name: 'Villa 2 Chambres', price: '185 900 €', beds: 2, baths: 2, size: 110, pool: '3×6m', roi: '13-15%' },
    { name: 'Villa 3 Chambres', price: '236 900 €', beds: 3, baths: 3, size: 160, pool: '3×7m', roi: '14-16%' }
  ];

  const benefits = [
    { icon: TrendingUp, title: '12-16% ROI', desc: 'Rendement locatif annuel' },
    { icon: Shield, title: 'Bail 30+20 ans', desc: 'Sécurité juridique' },
    { icon: Calendar, title: 'Livraison 2028', desc: 'Projet en cours' },
    { icon: Star, title: 'Clé en main', desc: 'Entièrement meublé' }
  ];

  const scrollToForm = () => {
    document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-[#F5F3EF]">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src="/images/terra-sky/clean_28_0.jpeg" 
            alt="Villa Bali" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1A1A2E]/90 via-[#1A1A2E]/70 to-transparent" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-2xl">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-[#C9A227]/20 border border-[#C9A227]/50 rounded-full px-4 py-2 mb-6">
              <span className="w-2 h-2 bg-[#C9A227] rounded-full animate-pulse" />
              <span className="text-[#C9A227] text-sm font-medium">Opportunité Exclusive</span>
            </div>

            {/* Title */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-playfair text-white font-bold leading-tight mb-4">
              Investissez dans une
              <span className="text-[#C9A227]"> Villa à Bali</span>
            </h1>

            <p className="text-xl text-white/80 mb-8 leading-relaxed">
              Rendement locatif de <strong className="text-[#C9A227]">12 à 16% par an</strong>. 
              Villas avec piscine privée, entièrement meublées, livrées clé en main.
            </p>

            {/* Price Tag */}
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 mb-8 border border-white/20">
              <p className="text-white/60 text-sm mb-1">À partir de</p>
              <p className="text-4xl font-bold text-white mb-2">116 900 €</p>
              <p className="text-[#C9A227] text-sm">Paiement échelonné disponible</p>
            </div>

            {/* CTA Button */}
            <button
              onClick={scrollToForm}
              className="group w-full sm:w-auto bg-[#C9A227] hover:bg-[#B8922B] text-white font-semibold py-4 px-8 rounded-xl flex items-center justify-center gap-3 transition-all shadow-lg shadow-[#C9A227]/30"
              data-testid="hero-cta"
            >
              Recevoir les informations
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>

            {/* Trust indicators */}
            <div className="flex items-center gap-6 mt-8 text-white/60 text-sm">
              <span className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-[#C9A227]" />
                Sans engagement
              </span>
              <span className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-[#C9A227]" />
                Réponse sous 24h
              </span>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <ChevronDown className="w-8 h-8 text-white/50" />
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <div key={index} className="text-center p-6 rounded-2xl bg-[#F5F3EF]">
                <div className="w-14 h-14 bg-[#C9A227]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <benefit.icon className="w-7 h-7 text-[#C9A227]" />
                </div>
                <h3 className="text-xl font-bold text-[#1A1A2E] mb-1">{benefit.title}</h3>
                <p className="text-[#4A4A5A] text-sm">{benefit.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Project Section */}
      <section className="py-20 bg-[#F5F3EF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-[#C9A227] font-medium mb-2">PROJET EXCLUSIF</p>
            <h2 className="text-3xl sm:text-4xl font-playfair text-[#1A1A2E] font-bold mb-4">
              Kaba-Kaba × Bali Life
            </h2>
            <p className="text-[#4A4A5A] max-w-2xl mx-auto">
              Résidence privée de standing à 3 minutes de la plage de Seseh. 
              Architecture contemporaine, nature et intimité.
            </p>
          </div>

          {/* Location badges */}
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {['Plage Seseh — 3 min', 'Canggu — 10 min', 'Seminyak — 25 min', 'Aéroport — 35 min'].map((loc, i) => (
              <span key={i} className="flex items-center gap-2 bg-white px-4 py-2 rounded-full text-sm text-[#4A4A5A]">
                <MapPin className="w-4 h-4 text-[#C9A227]" />
                {loc}
              </span>
            ))}
          </div>

          {/* Unit Selector */}
          <div className="flex justify-center gap-3 mb-8">
            {units.map((unit, index) => (
              <button
                key={index}
                onClick={() => setActiveUnit(index)}
                className={`px-6 py-3 rounded-xl font-medium transition-all ${
                  activeUnit === index
                    ? 'bg-[#C9A227] text-white shadow-lg'
                    : 'bg-white text-[#4A4A5A] hover:bg-[#C9A227]/10'
                }`}
              >
                {unit.beds} CH
              </button>
            ))}
          </div>

          {/* Unit Card */}
          <div className="max-w-4xl mx-auto bg-white rounded-3xl shadow-xl overflow-hidden">
            <div className="grid md:grid-cols-2">
              <div className="aspect-square md:aspect-auto">
                <img 
                  src="/images/terra-sky/clean_20_1.jpeg" 
                  alt={units[activeUnit].name}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-8 flex flex-col justify-center">
                <h3 className="text-2xl font-bold text-[#1A1A2E] mb-2">{units[activeUnit].name}</h3>
                <p className="text-[#C9A227] text-3xl font-bold mb-6">{units[activeUnit].price}</p>
                
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="flex items-center gap-3 p-3 bg-[#F5F3EF] rounded-xl">
                    <Bed className="w-5 h-5 text-[#C9A227]" />
                    <div>
                      <p className="text-xs text-[#8A8A9A]">Chambres</p>
                      <p className="font-bold text-[#1A1A2E]">{units[activeUnit].beds}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-[#F5F3EF] rounded-xl">
                    <Bath className="w-5 h-5 text-[#C9A227]" />
                    <div>
                      <p className="text-xs text-[#8A8A9A]">SDB</p>
                      <p className="font-bold text-[#1A1A2E]">{units[activeUnit].baths}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-[#F5F3EF] rounded-xl">
                    <Maximize className="w-5 h-5 text-[#C9A227]" />
                    <div>
                      <p className="text-xs text-[#8A8A9A]">Surface</p>
                      <p className="font-bold text-[#1A1A2E]">{units[activeUnit].size} m²</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-[#F5F3EF] rounded-xl">
                    <Waves className="w-5 h-5 text-[#C9A227]" />
                    <div>
                      <p className="text-xs text-[#8A8A9A]">Piscine</p>
                      <p className="font-bold text-[#1A1A2E]">{units[activeUnit].pool}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-emerald-50 rounded-xl p-4 mb-6">
                  <p className="text-emerald-800 font-medium flex items-center gap-2">
                    <TrendingUp className="w-5 h-5" />
                    ROI estimé : <strong>{units[activeUnit].roi}</strong> / an
                  </p>
                </div>

                <button
                  onClick={scrollToForm}
                  className="w-full bg-[#1A1A2E] hover:bg-[#2A2A3E] text-white font-semibold py-4 rounded-xl flex items-center justify-center gap-2 transition-colors"
                >
                  Demander la brochure
                  <ArrowRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-playfair text-[#1A1A2E] font-bold text-center mb-8">
            Découvrez le projet en images
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              '/images/terra-sky/clean_15_0.jpeg',
              '/images/terra-sky/clean_18_1.jpeg',
              '/images/terra-sky/clean_22_2.jpeg',
              '/images/terra-sky/clean_30_1.jpeg'
            ].map((img, i) => (
              <div key={i} className="aspect-square rounded-2xl overflow-hidden">
                <img src={img} alt="" className="w-full h-full object-cover hover:scale-110 transition-transform duration-500" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section id="contact-form" className="py-20 bg-[#1A1A2E]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <p className="text-[#C9A227] font-medium mb-2">PARLONS DE VOTRE PROJET</p>
            <h2 className="text-3xl sm:text-4xl font-playfair text-white font-bold mb-4">
              Recevez toutes les informations
            </h2>
            <p className="text-white/70">
              Remplissez le formulaire ci-dessous et notre équipe vous contactera sous 24h.
            </p>
          </div>

          {isSubmitted ? (
            <div className="bg-emerald-500/20 border border-emerald-500/50 rounded-2xl p-8 text-center">
              <CheckCircle className="w-16 h-16 text-emerald-400 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-white mb-2">Merci pour votre intérêt !</h3>
              <p className="text-white/70">
                Notre équipe vous contactera dans les prochaines 24 heures.
              </p>
            </div>
          ) : submitError ? (
            <div className="bg-amber-500/20 border border-amber-500/50 rounded-2xl p-8 text-center">
              <Mail className="w-16 h-16 text-amber-400 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-white mb-2">Merci pour votre demande !</h3>
              <p className="text-white/70 mb-4">
                Suite à un problème technique, veuillez nous contacter directement :
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <a href="mailto:hello@balilifeconcierge.com" className="flex items-center gap-2 text-[#C9A227] hover:underline">
                  <Mail className="w-5 h-5" />
                  hello@balilifeconcierge.com
                </a>
                <a href="tel:+6282123631370" className="flex items-center gap-2 text-[#C9A227] hover:underline">
                  <Phone className="w-5 h-5" />
                  +62 82123631370
                </a>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10">
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-white/80 text-sm mb-2">Nom complet *</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:border-[#C9A227] focus:outline-none transition-colors"
                    placeholder="Votre nom"
                    data-testid="form-name"
                  />
                </div>
                <div>
                  <label className="block text-white/80 text-sm mb-2">Email *</label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:border-[#C9A227] focus:outline-none transition-colors"
                    placeholder="votre@email.com"
                    data-testid="form-email"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-white/80 text-sm mb-2">Téléphone</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:border-[#C9A227] focus:outline-none transition-colors"
                    placeholder="+33 6 00 00 00 00"
                    data-testid="form-phone"
                  />
                </div>
                <div>
                  <label className="block text-white/80 text-sm mb-2">Budget envisagé</label>
                  <select
                    value={formData.budget}
                    onChange={(e) => setFormData({...formData, budget: e.target.value})}
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white focus:border-[#C9A227] focus:outline-none transition-colors"
                    data-testid="form-budget"
                  >
                    <option value="" className="bg-[#1A1A2E]">Sélectionnez</option>
                    <option value="100-150k" className="bg-[#1A1A2E]">100 000 € - 150 000 €</option>
                    <option value="150-200k" className="bg-[#1A1A2E]">150 000 € - 200 000 €</option>
                    <option value="200-300k" className="bg-[#1A1A2E]">200 000 € - 300 000 €</option>
                    <option value="300k+" className="bg-[#1A1A2E]">Plus de 300 000 €</option>
                  </select>
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-white/80 text-sm mb-2">Message (optionnel)</label>
                <textarea
                  value={formData.message}
                  onChange={(e) => setFormData({...formData, message: e.target.value})}
                  rows={4}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:border-[#C9A227] focus:outline-none transition-colors resize-none"
                  placeholder="Parlez-nous de votre projet d'investissement..."
                  data-testid="form-message"
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#C9A227] hover:bg-[#B8922B] disabled:opacity-50 text-white font-semibold py-4 rounded-xl flex items-center justify-center gap-2 transition-colors shadow-lg shadow-[#C9A227]/30"
                data-testid="form-submit"
              >
                {isSubmitting ? (
                  'Envoi en cours...'
                ) : (
                  <>
                    Envoyer ma demande
                    <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </button>

              <p className="text-white/40 text-xs text-center mt-4">
                En soumettant ce formulaire, vous acceptez d'être contacté par notre équipe.
              </p>
            </form>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-[#0F0F1A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#C9A227] to-[#B8922B] flex items-center justify-center">
                <span className="text-white font-bold">BL</span>
              </div>
              <div>
                <p className="text-white font-semibold">Bali Life Concierge</p>
                <p className="text-white/50 text-xs">Immobilier & Conciergerie</p>
              </div>
            </div>
            
            <div className="flex items-center gap-6 text-white/60 text-sm">
              <a href="mailto:hello@balilifeconcierge.com" className="flex items-center gap-2 hover:text-[#C9A227] transition-colors">
                <Mail className="w-4 h-4" />
                hello@balilifeconcierge.com
              </a>
              <a href="tel:+6282123631370" className="flex items-center gap-2 hover:text-[#C9A227] transition-colors">
                <Phone className="w-4 h-4" />
                +62 82123631370
              </a>
            </div>
          </div>
          
          <div className="border-t border-white/10 mt-6 pt-6 text-center">
            <p className="text-white/40 text-xs">
              © 2024 Bali Life Concierge - PT Global Trade Belindo - Tous droits réservés
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingImmo;
